import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  Modal,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { tenderApi, type PackageDetail, type PackageSummary, type Supplement, type TenderPackage } from '../../api';
import { PACKAGE_STATUS, PROCEDURES, SUPPLEMENT_STATUS, formatDate, formatEuro, formatPercent, todayIso, type PackageStatusKey } from '../../format';
import { EditableText } from '../../ui/EditableText';
import { useContextMenu } from '../../ui/ContextMenu';
import { Section } from '../../ui/Section';
import { BiddersPanel } from './BiddersPanel';
import { BoqPanel } from './BoqPanel';
import { ComparisonPanel } from './ComparisonPanel';
import { errorText, useTenderMutation } from './usePackage';

/**
 * Tendering and award (module 6).
 *
 * Laid out as tendering is worked through: award units on the left, the one
 * in hand on the right, and its steps as tabs in their natural order - what is
 * tendered, who was asked, how the offers compare, who gets the order.
 */
/**
 * Tendering and award, as two steps of one process (the usual AVA sequence):
 * "ausschreibung" prepares and sends - details, bill of quantities, bidders
 * asked; "vergabe" decides - offers, price comparison, award. Both work on the
 * same award units (one module, ADR 0004); the list on the left is shared.
 */
export type TenderStage = 'ausschreibung' | 'vergabe';

export function TenderingPage({ stage = 'vergabe' }: { stage?: TenderStage }): JSX.Element {
  const { projectId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const selected = params.get('v');
  const select = (id: string | null): void => setParams(id ? { v: id } : {}, { replace: true });
  const queryClient = useQueryClient();
  const me = queryClient.getQueryData<{ permissions: string[] }>(['me']);
  const canEdit = me?.permissions.includes('tendering.write') ?? false;
  const canAward = me?.permissions.includes('tendering.award') ?? false;
  const narrow = useMediaQuery('(max-width: 62em)');
  const list = useQuery({ queryKey: ['tenders', projectId], queryFn: () => tenderApi.list(projectId) });
  const [creating, setCreating] = useState(false);

  const refreshList = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['tenders', projectId] });
  const sample = useMutation({ mutationFn: () => tenderApi.sample(projectId), onSuccess: async ({ id }) => { await refreshList(); select(id); } });
  const duplicate = useMutation({ mutationFn: (id: string) => tenderApi.duplicate(projectId, id), onSuccess: async ({ id }) => { await refreshList(); select(id); } });
  const remove = useMutation({
    mutationFn: (id: string) => tenderApi.remove(projectId, id),
    onSuccess: async () => { select(null); await refreshList(); await queryClient.invalidateQueries({ queryKey: ['costs', projectId] }); },
  });
  const [confirmDelete, setConfirmDelete] = useState<PackageSummary | null>(null);

  useEffect(() => {
    if (!selected && !narrow && list.data && list.data.length > 0) select(list.data[0]!.id);
  }, [list.data, selected, narrow]);

  const context = useContextMenu<PackageSummary>((p, close) => (
    <>
      <Menu.Label>{p.code} {p.name}</Menu.Label>
      <Menu.Item onClick={() => { close(); select(p.id); }}>Öffnen</Menu.Item>
      {canEdit && <Menu.Item onClick={() => { close(); duplicate.mutate(p.id); }}>Duplizieren (mit LV, ohne Bietende)</Menu.Item>}
      <Menu.Item component="a" href={tenderApi.lvCsvUrl(projectId, p.id)} onClick={close}>LV als CSV</Menu.Item>
      <Menu.Item component="a" href={tenderApi.comparisonCsvUrl(projectId, p.id)} onClick={close}>Preisspiegel als CSV</Menu.Item>
      {canEdit && (
        <>
          <Menu.Divider />
          <Menu.Item color="red" onClick={() => { close(); setConfirmDelete(p); }}>Löschen …</Menu.Item>
        </>
      )}
    </>
  ));

  if (list.isPending) return <Center h={200}><Loader /></Center>;
  if (list.isError) return <Alert color="red">Die Vergaben konnten nicht geladen werden.</Alert>;
  const packages = list.data;
  const showList = !narrow || !selected;
  const showDetail = selected && (!narrow || selected);

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      <Group align="flex-start" gap="md" wrap="nowrap" pb="xl">
        {showList && (
          <Stack gap="xs" w={narrow ? '100%' : 300} style={{ flexShrink: 0 }}>
            <Group justify="space-between">
              <Title order={5}>Vergabeeinheiten</Title>
              {canEdit && <Button size="xs" onClick={() => setCreating(true)}>+ Neu</Button>}
            </Group>
            {packages.length === 0 && (
              <Paper withBorder radius="md" p="md">
                <Stack gap="xs">
                  <Text size="sm">Noch keine Vergabeeinheit. Eine Vergabeeinheit ist, was gemeinsam ausgeschrieben und vergeben wird – meist ein Gewerk.</Text>
                  {canEdit && <Button variant="light" size="xs" onClick={() => sample.mutate()} loading={sample.isPending}>Beispiel zum Ausprobieren anlegen</Button>}
                  <Text size="xs" c="dimmed">Das Beispiel enthält fiktive Bietende und Preise und lässt sich wieder löschen.</Text>
                </Stack>
              </Paper>
            )}
            {packages.map((p) => (
              <PackageCard key={p.id} p={p} active={p.id === selected} onClick={() => select(p.id)} onContextMenu={(e) => context.open(e, p)} />
            ))}
            {packages.length > 0 && canEdit && (
              <Button variant="subtle" size="xs" color="gray" onClick={() => sample.mutate()} loading={sample.isPending}>+ Beispiel-Vergabe (fiktiv)</Button>
            )}
          </Stack>
        )}
        {showDetail && selected && (
          <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
            {narrow && <Button variant="subtle" size="xs" onClick={() => select(null)} style={{ alignSelf: 'flex-start' }}>← Alle Vergabeeinheiten</Button>}
            <PackageView key={`${stage}-${selected}`} stage={stage} projectId={projectId} packageId={selected} canEdit={canEdit} canAward={canAward} onDeleted={() => select(null)} />
          </Stack>
        )}
        {!selected && !narrow && packages.length > 0 && <Text c="dimmed">Vergabeeinheit wählen.</Text>}
      </Group>

      <CreateModal opened={creating} onClose={() => setCreating(false)} projectId={projectId} onCreated={async (id) => { setCreating(false); await refreshList(); select(id); }} />
      <Modal opened={confirmDelete !== null} onClose={() => setConfirmDelete(null)} title="Vergabeeinheit löschen" size="sm">
        <Stack>
          <Text size="sm">„{confirmDelete?.code} {confirmDelete?.name}“ wird mit LV, Bietenden und Preisen gelöscht. Ein Auftrag verschwindet auch aus der Kostenkontrolle.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDelete(null)}>Abbrechen</Button>
            <Button color="red" loading={remove.isPending} onClick={() => confirmDelete && remove.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) })}>Löschen</Button>
          </Group>
        </Stack>
      </Modal>
      {context.menu}
    </div>
  );
}

function PackageCard({ p, active, onClick, onContextMenu }: { p: PackageSummary; active: boolean; onClick: () => void; onContextMenu: (e: React.MouseEvent) => void }): JSX.Element {
  const status = PACKAGE_STATUS[p.status as PackageStatusKey];
  return (
    <UnstyledButton onClick={onClick} onContextMenu={onContextMenu} aria-current={active ? 'true' : undefined}>
      <Paper
        withBorder
        radius="md"
        p="sm"
        className="pst-tile"
        style={active ? { borderColor: 'var(--mantine-color-brand-5)', boxShadow: '0 0 0 1px var(--mantine-color-brand-5)' } : undefined}
      >
        <Group justify="space-between" wrap="nowrap" gap={6}>
          <Text size="xs" c="dimmed" ff="monospace">{p.code}{p.costGroup ? ` · KG ${p.costGroup}` : ''}</Text>
          <Badge size="xs" variant="light" color={status.color}>{status.label}</Badge>
        </Group>
        <Text fw={600} size="sm" lineClamp={2} mt={2}>{p.name}</Text>
        <Text size="xs" c="dimmed" mt={4}>
          {p.positions} Pos. · {p.submitted}/{p.bidders} Angebote{p.deadline ? ` · Frist ${formatDate(p.deadline)}` : ''}
        </Text>
        {p.awarded ? (
          <Text size="xs" mt={2}><Text span c="green.8" fw={600}>vergeben</Text> an {p.awarded.company} · {formatEuro(p.awarded.amount)}</Text>
        ) : p.lowest ? (
          <Text size="xs" mt={2} c="dimmed">günstigstes: {formatEuro(p.lowest.amount)}{p.estimate ? ` (${formatPercent(((p.lowest.amount - p.estimate) / p.estimate) * 100, true)} zur Schätzung)` : ''}</Text>
        ) : null}
      </Paper>
    </UnstyledButton>
  );
}

function PackageView({ stage, projectId, packageId, canEdit, canAward, onDeleted }: { stage: TenderStage; projectId: string; packageId: string; canEdit: boolean; canAward: boolean; onDeleted: () => void }): JSX.Element {
  const q = useQuery({ queryKey: ['tender', projectId, packageId], queryFn: () => tenderApi.get(projectId, packageId), retry: false });
  const update = useTenderMutation(projectId, packageId, (body: Parameters<typeof tenderApi.update>[2]) => tenderApi.update(projectId, packageId, body));
  const navigate = useNavigate();
  const [tab, setTab] = useState<string | null>('overview');
  const other = stage === 'ausschreibung' ? 'vergabe' : 'ausschreibung';
  useEffect(() => {
    if (q.isError) onDeleted();
  }, [q.isError]);
  if (q.isPending) return <Center h={200}><Loader /></Center>;
  if (q.isError) return <Alert color="red">Die Vergabeeinheit gibt es nicht mehr.</Alert>;
  const d = q.data;
  const p = d.package;
  const status = PACKAGE_STATUS[p.status as PackageStatusKey];
  const edit = canEdit;

  return (
    <Stack gap="sm">
      {update.isError && <Alert color="red">{errorText(update.error)}</Alert>}
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="xs">
        <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
          <Group gap="xs" wrap="nowrap">
            <EditableText value={p.code} monospace dimmed onCommit={(code) => edit && update.mutate({ code })} />
            {p.status === 'awarded' ? (
              <Badge variant="light" color={status.color}>{status.label}</Badge>
            ) : (
              <Menu withinPortal>
                <Menu.Target>
                  <Badge component="button" variant="light" color={status.color} style={{ cursor: edit ? 'pointer' : undefined, textTransform: 'none' }}>{status.label}</Badge>
                </Menu.Target>
                {edit && (
                  <Menu.Dropdown>
                    {(['draft', 'sent', 'offers', 'cancelled'] as const).map((s) => (
                      <Menu.Item key={s} onClick={() => update.mutate({ status: s })}>{PACKAGE_STATUS[s].label}</Menu.Item>
                    ))}
                  </Menu.Dropdown>
                )}
              </Menu>
            )}
          </Group>
          <EditableText value={p.name} size="lg" bold onCommit={(name) => edit && update.mutate({ name })} />
        </Stack>
        <Group gap="xs">
          {/* Sending ends the tendering step: status and date are set, the award step takes over. */}
          {stage === 'ausschreibung' && edit && p.status === 'draft' && (
            <Button size="xs" onClick={() => update.mutate({ status: 'sent', sentOn: p.sentOn ?? new Date().toISOString().slice(0, 10) }, { onSuccess: () => navigate(`../vergabe?v=${packageId}`) })}>
              Versandt – weiter zur Vergabe
            </Button>
          )}
          <Button size="xs" variant="default" onClick={() => navigate(`../${other}?v=${packageId}`)}>
            {stage === 'ausschreibung' ? 'Zur Vergabe →' : '← Zur Ausschreibung'}
          </Button>
        </Group>
      </Group>

      <Tabs value={tab} onChange={setTab} variant="outline" keepMounted={false}>
        {stage === 'ausschreibung' ? (
          <>
            <Tabs.List style={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
              <Tabs.Tab value="overview">Übersicht</Tabs.Tab>
              <Tabs.Tab value="lv">Leistungsverzeichnis ({d.items.filter((i) => i.kind === 'position').length})</Tabs.Tab>
              <Tabs.Tab value="bidders">Bieterkreis ({d.bidders.length})</Tabs.Tab>
            </Tabs.List>
            <Paper withBorder radius="md" p="md" style={{ borderTopLeftRadius: 0 }}>
              <Tabs.Panel value="overview"><Overview stage={stage} detail={d} canEdit={edit} onSave={(body) => update.mutate(body)} onGo={setTab} /></Tabs.Panel>
              <Tabs.Panel value="lv"><BoqPanel projectId={projectId} detail={d} canEdit={edit} /></Tabs.Panel>
              <Tabs.Panel value="bidders"><BiddersPanel mode="bieterkreis" projectId={projectId} detail={d} canEdit={edit} /></Tabs.Panel>
            </Paper>
          </>
        ) : (
          <>
            <Tabs.List style={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
              <Tabs.Tab value="overview">Übersicht</Tabs.Tab>
              <Tabs.Tab value="offers">Angebote ({d.bidders.filter((b) => b.status === 'submitted').length}/{d.bidders.length})</Tabs.Tab>
              <Tabs.Tab value="comparison">Preisspiegel</Tabs.Tab>
              <Tabs.Tab value="award">Zuschlag</Tabs.Tab>
            </Tabs.List>
            <Paper withBorder radius="md" p="md" style={{ borderTopLeftRadius: 0 }}>
              <Tabs.Panel value="overview"><Overview stage={stage} detail={d} canEdit={edit} onSave={(body) => update.mutate(body)} onGo={setTab} /></Tabs.Panel>
              <Tabs.Panel value="offers"><BiddersPanel mode="angebote" projectId={projectId} detail={d} canEdit={edit} /></Tabs.Panel>
              <Tabs.Panel value="comparison"><ComparisonPanel projectId={projectId} detail={d} /></Tabs.Panel>
              <Tabs.Panel value="award"><AwardPanel projectId={projectId} detail={d} canAward={canAward} canEdit={edit} /></Tabs.Panel>
            </Paper>
          </>
        )}
      </Tabs>
    </Stack>
  );
}

function Overview({ stage, detail, canEdit, onSave, onGo }: { stage: TenderStage; detail: PackageDetail; canEdit: boolean; onSave: (body: Partial<TenderPackage>) => void; onGo: (tab: string) => void }): JSX.Element {
  const p = detail.package;
  const [draft, setDraft] = useState(p);
  useEffect(() => setDraft(p), [p]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(p);
  const field = (key: keyof TenderPackage) => ({
    value: (draft[key] as string | null) ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Read now; the updater runs after React cleared currentTarget.
      const value = e.currentTarget.value || null;
      setDraft((d) => ({ ...d, [key]: value }));
    },
    readOnly: !canEdit,
  });
  const lowest = detail.evaluation.bidders.find((r) => r.rank === 1);
  const lowestBidder = lowest ? detail.bidders.find((b) => b.id === lowest.bidderId) : undefined;
  const awarded = detail.bidders.find((b) => b.id === p.awardedBidderId);
  const awardedResult = detail.evaluation.bidders.find((r) => r.bidderId === p.awardedBidderId);

  return (
    <Stack gap="md">
      {stage === 'ausschreibung' ? (
        <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
          <Kpi label="Positionen" value={String(detail.items.filter((i) => i.kind === 'position').length)} onClick={() => onGo('lv')} />
          <Kpi label="Schätzung netto" value={detail.evaluation.estimateTotal === null ? '—' : formatEuro(detail.evaluation.estimateTotal)} onClick={() => onGo('lv')} />
          <Kpi label="Bieterkreis" value={String(detail.bidders.length)} note={detail.bidders.filter((b) => b.email).length < detail.bidders.length ? 'nicht alle mit E-Mail' : undefined} onClick={() => onGo('bidders')} />
          <Kpi label="Angebotsfrist" value={p.deadline ? formatDate(p.deadline) : '—'} note={p.sentOn ? `versandt ${formatDate(p.sentOn)}` : 'noch nicht versandt'} onClick={() => onGo('overview')} />
        </SimpleGrid>
      ) : (
      <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
        <Kpi label="Angebote" value={`${detail.bidders.filter((b) => b.status === 'submitted').length} von ${detail.bidders.length}`} note={p.deadline ? `Frist ${formatDate(p.deadline)}` : undefined} onClick={() => onGo('offers')} />
        <Kpi label="Schätzung netto" value={detail.evaluation.estimateTotal === null ? '—' : formatEuro(detail.evaluation.estimateTotal)} onClick={() => onGo('comparison')} />
        <Kpi label="Bindefrist" value={p.bindingUntil ? formatDate(p.bindingUntil) : '—'} onClick={() => onGo('award')} />
        {awarded && awardedResult ? (
          <Kpi label="Vergeben an" value={formatEuro(awardedResult.netAfterDiscount)} note={awarded.company} onClick={() => onGo('award')} tone="green" />
        ) : (
          <Kpi label="Günstigstes Angebot" value={lowest ? formatEuro(lowest.netAfterDiscount) : '—'} note={lowestBidder ? `${lowestBidder.company}${lowest?.againstEstimatePercent !== null && lowest ? ` · ${formatPercent(lowest.againstEstimatePercent, true)} zur Schätzung` : ''}` : undefined} onClick={() => onGo('comparison')} />
        )}
      </SimpleGrid>
      )}

      {stage === 'ausschreibung' && <Section
        title="Angaben zur Ausschreibung"
        actions={canEdit && dirty ? (
          <>
            <Button size="xs" variant="default" onClick={() => setDraft(p)}>Verwerfen</Button>
            <Button size="xs" onClick={() => {
              const { id: _i, awardedBidderId: _a, awardedOn: _o, awardNote: _n, status: _s, ...rest } = draft;
              void _i; void _a; void _o; void _n; void _s;
              onSave({ ...rest, code: undefined, name: undefined } as Partial<TenderPackage>);
            }}>Speichern</Button>
          </>
        ) : undefined}
      >
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm" verticalSpacing="xs">
          <Select
            label="Verfahren"
            data={PROCEDURES.map((x) => ({ value: x.value, label: x.label }))}
            value={draft.procedure}
            onChange={(v) => v && setDraft((d) => ({ ...d, procedure: v }))}
            readOnly={!canEdit}
            allowDeselect={false}
          />
          <TextInput label="Kostengruppe (DIN 276)" placeholder="z. B. 340" {...field('costGroup')} />
          <Select
            label="Umsatzsteuer"
            data={['19', '7', '0'].map((v) => ({ value: v, label: `${v} %` }))}
            value={String(draft.vatRate)}
            onChange={(v) => v && setDraft((d) => ({ ...d, vatRate: Number(v) }))}
            readOnly={!canEdit}
            allowDeselect={false}
          />
          <TextInput type="date" label="Versand der Unterlagen" {...field('sentOn')} />
          <TextInput type="date" label="Angebotsfrist" {...field('deadline')} />
          <TextInput type="date" label="Bindefrist" {...field('bindingUntil')} />
          <TextInput type="date" label="Ausführung ab" {...field('workStart')} />
          <TextInput type="date" label="Ausführung bis" {...field('workEnd')} />
        </SimpleGrid>
        <Textarea label="Beschreibung, Hinweise" autosize minRows={2} maxRows={8} {...field('description')} />
        <Text size="xs" c="dimmed">Mit der Kostengruppe landet der Auftragswert beim Zuschlag in der Kostenkontrolle.</Text>
      </Section>}
      {stage === 'vergabe' && (
        <Text size="sm" c="dimmed">
          Verfahren, Fristen und Leistungsverzeichnis stehen im Reiter Ausschreibung. Hier: Angebote erfassen, im Preisspiegel werten, Zuschlag erteilen – der Auftragswert geht in die Kostenkontrolle.
        </Text>
      )}
    </Stack>
  );
}

function Kpi({ label, value, note, onClick, tone }: { label: string; value: string; note?: string; onClick: () => void; tone?: 'green' }): JSX.Element {
  return (
    <UnstyledButton onClick={onClick}>
      <Paper withBorder radius="md" p="sm" className="pst-tile" style={tone ? { borderColor: 'var(--mantine-color-green-5)' } : undefined}>
        <Text size="xs" c="dimmed">{label}</Text>
        <Text fw={600} size="lg" className="pst-tabular">{value}</Text>
        {note && <Text size="xs" c="dimmed" lineClamp={1}>{note}</Text>}
      </Paper>
    </UnstyledButton>
  );
}

function AwardPanel({ projectId, detail, canAward, canEdit }: { projectId: string; detail: PackageDetail; canAward: boolean; canEdit: boolean }): JSX.Element {
  const p = detail.package;
  const ranked = detail.evaluation.bidders.filter((r) => r.rank !== null).sort((a, b) => a.rank! - b.rank!);
  const [bidderId, setBidderId] = useState<string | null>(detail.evaluation.lowestBidderId);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const award = useTenderMutation(projectId, p.id, () => tenderApi.award(projectId, p.id, { bidderId: bidderId!, awardedOn: date, note: note.trim() || null }));
  const withdraw = useTenderMutation(projectId, p.id, () => tenderApi.withdrawAward(projectId, p.id));
  const byId = new Map(detail.bidders.map((b) => [b.id, b]));
  const chosen = ranked.find((r) => r.bidderId === bidderId);
  const notLowest = chosen && chosen.rank !== 1;

  if (p.status === 'awarded' && p.awardedBidderId) {
    const r = detail.evaluation.bidders.find((x) => x.bidderId === p.awardedBidderId);
    return (
      <Stack gap="sm">
        <Alert color="green" title={`Vergeben an ${byId.get(p.awardedBidderId)?.company ?? '—'}`}>
          Zuschlag am {formatDate(p.awardedOn)} über {formatEuro(r?.netAfterDiscount ?? 0)} netto ({formatEuro(r?.gross ?? 0)} brutto).
          {p.costGroup ? ` Der Auftrag steht in der Kostenkontrolle unter KG ${p.costGroup}.` : ' Ohne Kostengruppe steht der Auftrag in der Kostenkontrolle unter „ohne Zuordnung“.'}
        </Alert>
        {p.awardNote && <Text size="sm" style={{ whiteSpace: 'pre-line' }}><b>Begründung:</b> {p.awardNote}</Text>}
        {canAward && <Button variant="default" color="red" onClick={() => withdraw.mutate(undefined)} loading={withdraw.isPending} style={{ alignSelf: 'flex-start' }}>Zuschlag zurücknehmen</Button>}
        {withdraw.isError && <Alert color="red">{errorText(withdraw.error)}</Alert>}
        {/* Supplements need tendering.write, as on the server - not the award right. */}
        <SupplementsPanel projectId={projectId} packageId={p.id} order={r?.netAfterDiscount ?? 0} canEdit={canEdit} />
      </Stack>
    );
  }
  if (ranked.length === 0) return <Text size="sm" c="dimmed" ta="center" py="lg">Vergeben lässt sich, sobald ein gewertetes Angebot vorliegt.</Text>;
  return (
    <Stack gap="sm" maw={640}>
      <Select
        label="Zuschlag an"
        data={ranked.map((r) => ({ value: r.bidderId, label: `${r.rank}. ${byId.get(r.bidderId)?.company} – ${formatEuro(r.netAfterDiscount)} netto` }))}
        value={bidderId}
        onChange={setBidderId}
        allowDeselect={false}
        disabled={!canAward}
      />
      {notLowest && <Alert color="yellow" py={6}>Nicht das günstigste Angebot ({formatPercent(chosen!.aboveLowestPercent, true)}). Die Begründung ist dann besonders wichtig.</Alert>}
      {chosen && chosen.missing > 0 && <Alert color="orange" py={6}>Dieses Angebot hat {chosen.missing} Positionen ohne Preis.</Alert>}
      <TextInput type="date" label="Datum des Zuschlags" value={date} onChange={(e) => setDate(e.currentTarget.value)} w={200} disabled={!canAward} />
      <Textarea label="Begründung" description="z. B. wirtschaftlichstes Angebot, Eignung geprüft, Nebenangebote nicht zugelassen" autosize minRows={3} value={note} onChange={(e) => setNote(e.currentTarget.value)} disabled={!canAward} />
      {award.isError && <Alert color="red">{errorText(award.error)}</Alert>}
      {canAward ? (
        <Button onClick={() => award.mutate(undefined)} disabled={!bidderId || !date} loading={award.isPending} style={{ alignSelf: 'flex-start' }}>Zuschlag erteilen</Button>
      ) : (
        <Text size="xs" c="dimmed">Den Zuschlag erteilen Projektleitung oder Administration.</Text>
      )}
      <Text size="xs" c="dimmed">PST hält die Entscheidung fest und überträgt den Auftragswert in die Kostenkontrolle. Ein Auftragsschreiben erzeugt PST nicht.</Text>
    </Stack>
  );
}

function CreateModal({ opened, onClose, projectId, onCreated }: { opened: boolean; onClose: () => void; projectId: string; onCreated: (id: string) => void }): JSX.Element {
  const [name, setName] = useState('');
  const [costGroup, setCostGroup] = useState('');
  const [procedure, setProcedure] = useState<string>('restricted');
  const create = useMutation({
    mutationFn: () => tenderApi.create(projectId, { name: name.trim(), costGroup: costGroup.trim() || null, procedure }),
    onSuccess: ({ id }) => { setName(''); setCostGroup(''); onCreated(id); },
  });
  return (
    <Modal opened={opened} onClose={onClose} title="Vergabeeinheit anlegen">
      <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate(); }}>
        <Stack gap="sm">
          {create.isError && <Alert color="red">{errorText(create.error)}</Alert>}
          <TextInput label="Bezeichnung" placeholder="z. B. Rohbauarbeiten" required value={name} onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
          <TextInput label="Kostengruppe (DIN 276)" placeholder="z. B. 300 oder 340" value={costGroup} onChange={(e) => setCostGroup(e.currentTarget.value)} />
          <Select label="Verfahren" data={PROCEDURES.map((x) => ({ value: x.value, label: x.label }))} value={procedure} onChange={(v) => v && setProcedure(v)} allowDeselect={false} />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={!name.trim()} loading={create.isPending}>Anlegen</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}


const EMPTY_SUPPLEMENT: Omit<Supplement, 'id'> = { number: '', title: '', reason: '', status: 'angemeldet', submittedOn: null, offeredNet: null, checkedNet: null, approvedNet: null, approvedOn: null, note: '' };

/**
 * Supplements (Nachträge) to the awarded order (AP-106): announced, offered,
 * checked, commissioned or rejected. A commissioned one adds its amount to
 * the order in cost control.
 */
function SupplementsPanel({ projectId, packageId, order, canEdit }: { projectId: string; packageId: string; order: number; canEdit: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['supplements', projectId, packageId], queryFn: () => tenderApi.supplements(projectId, packageId) });
  const refresh = (): Promise<void> => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['supplements', projectId, packageId] }),
    queryClient.invalidateQueries({ queryKey: ['costs', projectId] }),
  ]).then(() => undefined);
  const [editing, setEditing] = useState<Supplement | 'new' | null>(null);
  const remove = useMutation({ mutationFn: (id: string) => tenderApi.removeSupplement(projectId, packageId, id), onSettled: refresh });
  const rows = list.data ?? [];
  const approved = rows.filter((s) => s.status === 'beauftragt').reduce((sum, s) => sum + (s.approvedNet ?? 0), 0);
  const open = rows.filter((s) => s.status !== 'beauftragt' && s.status !== 'abgelehnt').reduce((sum, s) => sum + (s.checkedNet ?? s.offeredNet ?? 0), 0);
  const nextNumber = `N${String(rows.length + 1).padStart(2, '0')}`;
  return (
    <Section title="Nachträge" description="Zum vergebenen Auftrag. Ein beauftragter Nachtrag erhöht den Auftrag in der Kostenkontrolle um den beauftragten Betrag.">
      <Group gap="lg" mb="xs">
        <Text size="sm">Auftrag {formatEuro(order)} · beauftragte Nachträge {formatEuro(approved)} · <b>zusammen {formatEuro(order + approved)}</b> netto</Text>
        {open > 0 && <Text size="sm" c="orange.8">offen (angeboten/geprüft): {formatEuro(open)}</Text>}
        {canEdit && <Button size="xs" variant="light" onClick={() => setEditing('new')}>+ Nachtrag</Button>}
      </Group>
      {rows.length === 0 ? <Text size="sm" c="dimmed">Keine Nachträge.</Text> : (
        <Table verticalSpacing={3} highlightOnHover>
          <Table.Thead><Table.Tr><Table.Th w={30} /><Table.Th w={70}>Nr.</Table.Th><Table.Th>Nachtrag</Table.Th><Table.Th w={110}>Status</Table.Th><Table.Th w={120} ta="right">angeboten</Table.Th><Table.Th w={120} ta="right">geprüft</Table.Th><Table.Th w={120} ta="right">beauftragt</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {rows.map((s) => (
              <Table.Tr key={s.id} style={{ cursor: canEdit ? 'pointer' : undefined }} onClick={() => canEdit && setEditing(s)}>
                <Table.Td>{canEdit && <ActionIcon size="xs" variant="subtle" color="gray" aria-label="Nachtrag löschen" title="Nachtrag löschen" onClick={(e) => { e.stopPropagation(); if (window.confirm(`Nachtrag ${s.number} löschen?`)) remove.mutate(s.id); }}>✕</ActionIcon>}</Table.Td>
                <Table.Td><Text size="sm" ff="monospace">{s.number}</Text></Table.Td>
                <Table.Td><Text size="sm">{s.title}</Text>{s.reason && <Text size="xs" c="dimmed" lineClamp={1}>{s.reason}</Text>}</Table.Td>
                <Table.Td><Badge variant="light" color={SUPPLEMENT_STATUS[s.status].color}>{SUPPLEMENT_STATUS[s.status].label}</Badge></Table.Td>
                <Table.Td ta="right"><Text size="sm">{s.offeredNet === null ? '' : formatEuro(s.offeredNet)}</Text></Table.Td>
                <Table.Td ta="right"><Text size="sm">{s.checkedNet === null ? '' : formatEuro(s.checkedNet)}</Text></Table.Td>
                <Table.Td ta="right"><Text size="sm" fw={s.status === 'beauftragt' ? 700 : undefined}>{s.approvedNet === null ? '' : formatEuro(s.approvedNet)}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {editing && <SupplementModal projectId={projectId} packageId={packageId} supplement={editing === 'new' ? null : editing} nextNumber={nextNumber} onClose={() => setEditing(null)} onSaved={refresh} />}
    </Section>
  );
}

function SupplementModal({ projectId, packageId, supplement, nextNumber, onClose, onSaved }: { projectId: string; packageId: string; supplement: Supplement | null; nextNumber: string; onClose: () => void; onSaved: () => Promise<void> }): JSX.Element {
  const [form, setForm] = useState<Omit<Supplement, 'id'>>(() => (supplement ? (({ id: _, ...rest }) => rest)(supplement) : { ...EMPTY_SUPPLEMENT, number: nextNumber, submittedOn: todayIso() }));
  const save = useMutation({
    mutationFn: () => (supplement ? tenderApi.saveSupplement(projectId, packageId, supplement.id, form) : tenderApi.addSupplement(projectId, packageId, form).then(() => undefined)),
    onSuccess: async () => { await onSaved(); onClose(); },
  });
  const money = (key: 'offeredNet' | 'checkedNet' | 'approvedNet', label: string): JSX.Element => (
    <NumberInput label={label} value={form[key] ?? ''} min={0} decimalScale={2} thousandSeparator="." decimalSeparator="," onChange={(v) => setForm((f) => ({ ...f, [key]: v === '' ? null : Number(v) }))} />
  );
  return (
    <Modal opened onClose={onClose} title={supplement ? `Nachtrag ${supplement.number}` : 'Nachtrag anlegen'} size="lg">
      <Stack gap="xs">
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xs">
          <TextInput label="Nr." value={form.number} onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, number: v })); }} maxLength={20} />
          <Select label="Status" value={form.status} onChange={(v) => v && setForm((f) => ({ ...f, status: v as Supplement['status'], approvedOn: v === 'beauftragt' && !f.approvedOn ? todayIso() : f.approvedOn }))} allowDeselect={false} comboboxProps={{ withinPortal: true }}
            data={Object.entries(SUPPLEMENT_STATUS).map(([value, s]) => ({ value, label: s.label }))} />
          <TextInput label="eingereicht am" type="date" value={form.submittedOn ?? ''} onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, submittedOn: v || null })); }} />
        </SimpleGrid>
        <TextInput label="Gegenstand" value={form.title} onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, title: v })); }} maxLength={300} />
        <Textarea label="Grund" description="z. B. geänderte Leistung, zusätzliche Leistung, Behinderung – mit Verweis auf Anordnung oder Schriftverkehr" autosize minRows={2} value={form.reason} onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, reason: v })); }} />
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xs">
          {money('offeredNet', 'angeboten (€ netto)')}
          {money('checkedNet', 'geprüft (€ netto)')}
          {money('approvedNet', 'beauftragt (€ netto)')}
        </SimpleGrid>
        {form.status === 'beauftragt' && <TextInput label="beauftragt am" type="date" value={form.approvedOn ?? ''} onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, approvedOn: v || null })); }} w={200} />}
        <Textarea label="Notiz" autosize minRows={1} value={form.note} onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, note: v })); }} />
        <Text size="xs" c="dimmed">Keine Rechtsberatung: Ob und in welcher Höhe ein Nachtrag berechtigt ist (§ 2 VOB/B, § 650b BGB), ist im Einzelfall fachlich und rechtlich zu prüfen.</Text>
        {save.isError && <Alert color="red" py={6}>{errorText(save.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Abbrechen</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!form.number.trim() || !form.title.trim() || (form.status === 'beauftragt' && form.approvedNet === null)}>Speichern</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
